/**
 * Sprint 12+L4.8.3 (16.07.2026) — Bridge-Page nach Free-User-Pattern.
 *
 * Warum diese Seite?
 *   KT akzeptiert im Bestätigungsseite-URL-Feld KEINE Merge-Tags wie
 *   `{{custom_field_pionier_confirm_token}}` — nur die statische URL.
 *   KT hängt aber automatisch `?subscriberKey=<KEY>` an. Diese Bridge
 *   verwandelt den KT-subscriberKey in unseren eigenen `pionier_confirm_token`
 *   (via `/api/warteliste/verify?subscriberKey=…`) und redirected dann
 *   zu `/warteliste/danke?token=<UNSER_TOKEN>` — dort läuft die vollständige
 *   Personalisierung inkl. Library-Preview.
 *
 * Route: `/bestaetige-deine-email`
 * States: loading (Verify-Call läuft) / invalid (Fehler-Anzeige)
 * Success: window.location.href-Redirect (kein React-Navigate, damit die
 *   Danke-Seite mit frischer URL + Token-Query-Param neu gemountet wird).
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import ConfirmHintPage from "@/pages/ConfirmHintPage";

const STATE_LOADING = "loading";
const STATE_INVALID = "invalid";
const STATE_FALLBACK = "fallback"; // Free-User: kein subscriberKey oder kein Warteliste-Match

export default function BestaetigeDeineEmail() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState(STATE_LOADING);
  const [reason, setReason] = useState("");

  useEffect(() => {
    const subscriberKey = (searchParams.get("subscriberKey") || "").trim();
    if (!subscriberKey) {
      // Ohne subscriberKey: Free-User-Fallback (unveränderte "Fast geschafft!"-UI)
      setState(STATE_FALLBACK);
      return;
    }

    (async () => {
      try {
        const res = await api.get(
          `/warteliste/verify?subscriberKey=${encodeURIComponent(subscriberKey)}`,
        );
        const data = res?.data || {};
        if (data.valid && data.token) {
          // Pionier-User: Redirect auf Danke-Seite mit unserem Token
          window.location.replace(
            `/warteliste/danke?token=${encodeURIComponent(data.token)}`,
          );
          return;
        }
        // Kein Pionier-Match → Free-User-Fallback (kein Fehler, KT-Standard-Flow)
        setReason(data.reason || "no_pionier_match");
        setState(STATE_FALLBACK);
      } catch (_err) {
        setReason("network_error");
        setState(STATE_INVALID);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Free-User-Fallback: zeigt die bestehende ConfirmHintPage (unverändert)
  if (state === STATE_FALLBACK) {
    return <ConfirmHintPage />;
  }

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
      data-testid="bestaetige-page"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10 text-center">
        {state === STATE_LOADING && <LoadingView />}
        {state === STATE_INVALID && <InvalidView reason={reason} />}
      </div>
    </div>
  );
}

function LoadingView() {
  return (
    <div data-testid="bestaetige-loading">
      <Loader2 className="w-10 h-10 text-[#4563BB] animate-spin mx-auto mb-4" />
      <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
        Bitte einen Moment …
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        Wir bestätigen deine E-Mail-Adresse.
      </p>
    </div>
  );
}

function InvalidView({ reason }) {
  const messageMap = {
    subscriber_key_missing:
      "In deiner URL fehlt der Bestätigungs-Schlüssel. Bitte klicke den Link aus deiner Bestätigungs-Mail direkt an.",
    subscriber_key_not_found:
      "Wir konnten deinen Bestätigungs-Schlüssel nicht mehr finden. Bitte klicke den Link aus deiner Original-Mail.",
    subscriber_key_lookup_unavailable:
      "Der KT-Lookup ist gerade nicht verfügbar. Bitte versuche es in ein paar Minuten noch einmal.",
    subscriber_key_lookup_failed:
      "Wir konnten deinen Schlüssel gerade nicht verifizieren. Bitte versuche es in einem Moment noch einmal.",
    verification_failed:
      "Deine Bestätigung konnte nicht abgeschlossen werden. Bitte klicke den Link aus deiner Mail erneut.",
    network_error:
      "Wir konnten den Server nicht erreichen. Bitte prüfe deine Verbindung und versuche es noch einmal.",
  };
  const msg = messageMap[reason] || messageMap.verification_failed;
  return (
    <div data-testid="bestaetige-invalid">
      <div className="inline-flex w-14 h-14 rounded-full bg-red-100 items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-red-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-3">
        Bestätigung fehlgeschlagen
      </h1>
      <p className="text-slate-600" data-testid="bestaetige-invalid-text">
        {msg}
      </p>
      <p className="mt-3 text-xs text-slate-400" data-testid="bestaetige-invalid-code">
        Fehler-Code: <span className="font-mono">{reason || "unknown"}</span>
      </p>
      <a
        href="/warteliste"
        className="mt-6 inline-flex items-center text-sm font-semibold text-[#4563BB] hover:underline"
        data-testid="bestaetige-back-link"
      >
        Zurück zur Warteliste →
      </a>
    </div>
  );
}
